import React from 'react';
import type {
  ColDef,
  ColGroupDef,
  IAggFuncParams,
  IRowNode,
  ValueGetterFunc,
  ValueGetterParams,
} from 'ag-grid-community';
import type { BulkManageRow } from '../types';
import { AllocationHeaderGroup } from '../columns/AllocationHeaderGroup';
import {
  AllocationCellClass,
  AllocationHeaderCellClass,
  AllocationHeaderGroupCellClass,
  AllocationTotalCellClass,
  BoldRowGroupCellClass,
  TaxInfoHeaderCellClass,
  TaxInfoHeaderGroupCellClass,
  TaxInfoHeaderFirstCellClass,
  TaxInfoHeaderRightAlignedCellClass,
  TaxInfoCellLeftAlignedClass,
} from '../columns';
import { isNil } from 'lodash';
import {
  isBulkManageHistoricalTotalRow,
  isBulkManageProFormaTotalRow,
  isBulkManageTotalRow,
  isHistoricalBulkManageInvestmentRow,
  isProFormaBulkManageInvestmentRow,
  isBulkManageLotRow,
} from '../utils';
import { getHistoricalPortfolioDateTotalAllocations, getProFormaPortfolioTotalNetAssets } from '../columns/utils';
import { type Portfolio, type HoldingPeriod, TAX_STATUS_LABELS } from 'venn-api';
import { autoGroupColDef } from '../columns/autoGroupColDef';
import { NameHeaderPrimitive } from '../columns/NameHeaderPrimitive';
import { formatAllocation, Dates, type AnalysisSubject, useHasFF } from 'venn-utils';
import { type AllocationValue, AllocationValueCellRenderer } from '../components/AllocationValueCellRenderer';
import { NameCellRenderer } from '../columns/NameCellRenderer';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { Tooltip, TooltipContentWithArrow, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { getAppTitle, Icon } from 'venn-ui-kit';
import { useRangeAnalysis } from './useRangeAnalysis';

type Props = {
  subject: AnalysisSubject;
  portfolio: Portfolio | undefined;
  selectedAllocationDate: string;
  allocationDates: number[];
  onSetSetSelectedDate: (date: string | undefined) => void;
  hasTaxInformation: boolean;
};
type AllocationColumns = {
  columnDefs: ColGroupDef<BulkManageRow>[];
  autoGroupColumnDef: ColDef<BulkManageRow>;
};

export const allocationValueGetter =
  (selectedAllocationDate: string): ValueGetterFunc<BulkManageRow, AllocationValue> =>
  ({ data }) => {
    if (isProFormaBulkManageInvestmentRow(data) || isBulkManageProFormaTotalRow(data)) {
      return { value: data.allocation ?? 0, isPercentage: false, isForwardFilled: false };
    }

    if (isHistoricalBulkManageInvestmentRow(data) || isBulkManageHistoricalTotalRow(data)) {
      // fill forward last allocation for display
      const lastAllocationDate = Object.keys(data.allocation)
        .map(Number)
        .sort((a, b) => a - b)
        .findLast((dt) => dt <= Number(selectedAllocationDate));
      const lastAllocation = !isNil(lastAllocationDate) ? data.allocation[lastAllocationDate] : undefined;
      return {
        value: lastAllocation ?? 0,
        isPercentage: false,
        isForwardFilled: lastAllocationDate !== Number(selectedAllocationDate),
      };
    }

    return { value: 0, isPercentage: false, isForwardFilled: true };
  };

export const weightAllocationValueGetter = (
  historical: boolean,
  proFormaTotalAllocations: number,
  portfolioDateTotalAllocations: Record<number, number>,
  selectedAllocationDate: string,
): ValueGetterFunc<BulkManageRow, AllocationValue> => {
  const allocationGetter = allocationValueGetter(selectedAllocationDate);
  return (params: ValueGetterParams<BulkManageRow, AllocationValue>): AllocationValue => {
    const allocation = allocationGetter(params);
    const node = params.node;
    const total = historical
      ? // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        portfolioDateTotalAllocations[selectedAllocationDate]
      : proFormaTotalAllocations;
    return (!node?.group || (node?.group && !node?.expanded)) && !isNil(allocation) && !isNil(total)
      ? {
          value: (allocation.value / Math.abs(total)) * 100.0,
          isForwardFilled: allocation.isForwardFilled,
          isPercentage: true,
        }
      : {
          value: 0,
          isForwardFilled: false,
          isPercentage: true,
        };
  };
};

export const quantityValueGetter = (): ValueGetterFunc<BulkManageRow, { value: number }> => {
  return ({ data }) => {
    if (isBulkManageLotRow(data)) {
      return { value: data.lot.quantity };
    }

    if (isBulkManageProFormaTotalRow(data)) {
      return { value: data.quantity ?? 0 };
    }

    return { value: 0 };
  };
};

const costBasisValueGetter = (): ValueGetterFunc<BulkManageRow, { value: number }> => {
  return ({ data }) => {
    if (isBulkManageLotRow(data)) {
      return { value: data.lot.costBasis };
    }

    if (isBulkManageProFormaTotalRow(data)) {
      return { value: data.costBasis ?? 0 };
    }

    return { value: 0 };
  };
};

export const taxStatusValueGetter = (): ValueGetterFunc<BulkManageRow, { value: string }> => {
  return ({ data }) => {
    if (isBulkManageLotRow(data)) {
      return { value: TAX_STATUS_LABELS[data.lot.taxStatus] };
    }
    return null;
  };
};

export const holdingPeriodValueGetter = (
  primaryAnalysisEnd?: number,
): ValueGetterFunc<BulkManageRow, { value: string | HoldingPeriod }> => {
  return ({ data }) => {
    const holdingPeriod = isBulkManageLotRow(data) ? data.lot.holdingPeriod : null;
    const datePurchased = holdingPeriod && 'datePurchased' in holdingPeriod ? holdingPeriod.datePurchased : undefined;

    return !holdingPeriod
      ? null
      : !datePurchased || isNil(primaryAnalysisEnd)
        ? { value: holdingPeriod }
        : {
            value:
              Dates.parse(datePurchased).add(1, 'year').valueOf() <= primaryAnalysisEnd ? 'Long Term' : 'Short Term',
          };
  };
};

export const datePurchasedValueGetter = (): ValueGetterFunc<BulkManageRow, { value: string }> => {
  return ({ data }) => {
    if (isBulkManageLotRow(data)) {
      const datePurchased = data.lot.holdingPeriod.datePurchased;
      return { value: datePurchased ? Dates.toDayMonthYear(datePurchased) : '' };
    }
    return null;
  };
};

export const allocationValueAggregator = (params: IAggFuncParams<BulkManageRow, AllocationValue>): AllocationValue => {
  let sum = 0;
  let isForwardFilled = false;
  let isPercentage = false;
  params.values.forEach((value) => {
    if (isNil(value)) {
      return;
    }

    sum += value.value;
    isForwardFilled = isForwardFilled || value.isForwardFilled;
    isPercentage = isPercentage || value.isPercentage;
  });
  return { value: sum, isForwardFilled, isPercentage };
};

export const quantityValueAggregator = (
  params: IAggFuncParams<BulkManageRow, { value: number }>,
): { value: number } => {
  let sum = 0;
  params.values.forEach((value) => {
    if (!isNil(value?.value)) {
      sum += value.value;
    }
  });
  return { value: sum };
};

const QuantityCellRenderer = ({ value }: CustomCellRendererProps<BulkManageRow>) => {
  return isNil(value) ? null : <span>{value.value}</span>;
};

const isLotGroup = ({ node, data }: { node?: IRowNode<BulkManageRow>; data?: BulkManageRow }) => {
  return !!(
    isProFormaBulkManageInvestmentRow(data) &&
    node?.childrenAfterFilter?.some((child) => isBulkManageLotRow(child.data))
  );
};

const TaxStatusCellRenderer = (params: CustomCellRendererProps<BulkManageRow>) => {
  if (isBulkManageLotRow(params.node.data)) {
    return isNil(params.value) ? null : <span>{params.value.value}</span>;
  }
  return null;
};

const CostBasisCellRenderer = (params: CustomCellRendererProps<BulkManageRow>) => {
  return isNil(params.value) ? null : <span>{formatAllocation(params.value?.value, true)}</span>;
};

const HoldingPeriodCellRenderer = (params: CustomCellRendererProps<BulkManageRow>) => {
  if (!isBulkManageLotRow(params.node.data) || isNil(params.value)) {
    return null;
  }

  const holdingPeriodValue = params.value.value;
  if (typeof holdingPeriodValue === 'string') {
    return <span>{holdingPeriodValue}</span>;
  }

  return <span>{holdingPeriodValue.type === 'LONG_TERM' ? 'Long Term' : 'Short Term'}</span>;
};

const HoldingPeriodHeaderCellRenderer = () => (
  <>
    Holding Period&nbsp;
    <Tooltip>
      <TooltipTrigger>
        <Icon className="text-venn-grey-80 hover:text-venn-dark-blue" prefix="far" type="circle-info" />
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContentWithArrow className="max-w-[220px]">
          {getAppTitle()} calculation based on Purchase Date and end date of Analysis Period selected. Otherwise,
          Holding Period is user provided.
        </TooltipContentWithArrow>
      </TooltipPortal>
    </Tooltip>
  </>
);

const DatePurchasedCellRenderer = (params: CustomCellRendererProps<BulkManageRow>) => {
  if (isBulkManageLotRow(params.node.data)) {
    return isNil(params.value) ? null : <span>{params.value.value}</span>;
  }
  return null;
};

export const useAllocationColumns = ({
  subject,
  portfolio,
  allocationDates,
  selectedAllocationDate,
  onSetSetSelectedDate,
  hasTaxInformation,
}: Props): AllocationColumns => {
  const hasTaxImpactFF = useHasFF('tax_impact_ff');
  const historical = !!portfolio?.historical;
  const portfolioDateTotalAllocations = getHistoricalPortfolioDateTotalAllocations(portfolio, allocationDates);
  const proFormaTotalAllocations = getProFormaPortfolioTotalNetAssets(portfolio);

  const { primaryAnalysis } = useRangeAnalysis(subject);

  const defaultAllocationCol: ColDef<BulkManageRow> = {
    flex: 1,
    minWidth: 100,
    maxWidth: 100,
    headerClass: AllocationHeaderCellClass,
    cellClass: AllocationCellClass,
    aggFunc: allocationValueAggregator,
    cellClassRules: {
      [BoldRowGroupCellClass]: ({ node, data }) =>
        (!!node?.group || isBulkManageTotalRow(data)) && !isLotGroup({ node, data }),
      [AllocationTotalCellClass]: ({ data }) => isBulkManageTotalRow(data),
    },
  };

  const autoGroupColumnDef = autoGroupColDef({
    customHeaderComponent: {
      headerComponent: NameHeaderPrimitive.Root,
    },
    showGroupHeader: false,
    showCheckbox: false,
    customInnerCellComponent: {
      innerRenderer: NameCellRenderer,
      innerRendererParams: {
        shouldShowHistoricalError: false,
      },
    },
  });

  return {
    columnDefs: [
      ...(!historical && hasTaxInformation && hasTaxImpactFF
        ? [
            {
              headerName: 'Tax Information',
              headerClass: TaxInfoHeaderGroupCellClass,
              children: [
                {
                  ...defaultAllocationCol,
                  headerName: 'Date Purchased',
                  headerClass: [TaxInfoHeaderCellClass, TaxInfoHeaderFirstCellClass],
                  cellClass: [TaxInfoCellLeftAlignedClass],
                  valueGetter: datePurchasedValueGetter(),
                  cellRenderer: DatePurchasedCellRenderer,
                },
                {
                  ...defaultAllocationCol,
                  headerName: 'Holding Period',
                  minWidth: 120,
                  maxWidth: 120,
                  headerComponent: HoldingPeriodHeaderCellRenderer,
                  headerClass: TaxInfoHeaderCellClass,
                  cellClass: [TaxInfoCellLeftAlignedClass],
                  valueGetter: holdingPeriodValueGetter(primaryAnalysis?.end),
                  cellRenderer: HoldingPeriodCellRenderer,
                },
                {
                  ...defaultAllocationCol,
                  headerName: 'Tax Status',
                  headerClass: TaxInfoHeaderCellClass,
                  cellClass: [TaxInfoCellLeftAlignedClass],
                  valueGetter: taxStatusValueGetter(),
                  cellRenderer: TaxStatusCellRenderer,
                },
                {
                  ...defaultAllocationCol,
                  headerName: 'Cost Basis',
                  headerClass: [TaxInfoHeaderCellClass, TaxInfoHeaderRightAlignedCellClass],
                  valueGetter: costBasisValueGetter(),
                  aggFunc: quantityValueAggregator,
                  cellRenderer: CostBasisCellRenderer,
                },
                {
                  ...defaultAllocationCol,
                  headerName: 'Quantity',
                  headerClass: [TaxInfoHeaderCellClass, TaxInfoHeaderRightAlignedCellClass],
                  valueGetter: quantityValueGetter(),
                  aggFunc: quantityValueAggregator,
                  cellRenderer: QuantityCellRenderer,
                },
              ],
            },
          ]
        : []),
      {
        headerName: 'Current Allocations',
        headerGroupComponent: AllocationHeaderGroup,
        headerGroupComponentParams: {
          historical,
          dates: allocationDates,
          selectedDate: selectedAllocationDate,
          setSelectedDate: onSetSetSelectedDate,
        },
        headerClass: AllocationHeaderGroupCellClass,
        children: [
          {
            ...defaultAllocationCol,
            headerName: 'Value',
            valueGetter: allocationValueGetter(selectedAllocationDate),
            valueFormatter: ({ value }) => {
              return !isNil(value?.value) ? `${formatAllocation(value?.value, true)}` : '';
            },
            cellRenderer: AllocationValueCellRenderer,
          },
          {
            ...defaultAllocationCol,
            headerName: 'Weight',
            valueGetter: weightAllocationValueGetter(
              historical,
              proFormaTotalAllocations,
              portfolioDateTotalAllocations,
              selectedAllocationDate,
            ),
            valueFormatter: ({ value }) => {
              return !isNil(value?.value) ? formatAllocation(value?.value, true, true, 100.0) : '';
            },
            cellRenderer: AllocationValueCellRenderer,
          },
        ],
      },
    ],
    autoGroupColumnDef,
  };
};
